import React, { useState, useRef, useCallback } from 'react';
import { Star, Paperclip, Mail, ChevronLeft, ChevronRight, Trash2, MailOpen, MailCheck, Archive, AlertOctagon, AlertTriangle, Check, X, Loader2 } from 'lucide-react';
import { cn, formatDate, truncate, getInitials } from '../lib/utils';
import {
  extractEmailDomain,
  isOwnSenderDomain,
  senderFaviconUrl,
  senderAvatarColor,
} from '../lib/senderAvatar';

const SenderAvatar = ({ email, selected, onSelect, selectionMode }) => {
  const [err, setErr] = useState(false);
  const domain = extractEmailDomain(email.from_email);
  const initials = getInitials(email.from_name || email.from_email || '?');

  if (selectionMode || selected) {
    return (
      <div
        onClick={e => { e.stopPropagation(); onSelect(); }}
        className={cn(
          "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 border-2 cursor-pointer transition-all",
          selected ? "bg-primary border-primary" : "border-border bg-muted hover:bg-primary/20"
        )}
      >
        {selected && <Check className="w-4 h-4 text-primary-foreground" />}
      </div>
    );
  }

  if (isOwnSenderDomain(domain)) {
    return <img src="/logo.png" alt="" className="w-8 h-8 rounded-full object-contain" loading="lazy" />;
  }
  const logo = senderFaviconUrl(domain);
  if (logo && !err) {
    return (
      <div className="w-8 h-8 rounded-full overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
        <img src={logo} alt="" className="w-5 h-5 object-contain" onError={() => setErr(true)} loading="lazy" referrerPolicy="no-referrer" />
      </div>
    );
  }
  const bg = senderAvatarColor(email.from_email);
  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: bg }}>
      {initials}
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, totalCount, pageSize, onPageChange }) => {
  if (totalPages <= 1) return null;
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalCount);
  return (
    <div className="flex items-center justify-end gap-2 px-4 py-1.5 border-b border-border text-sm text-muted-foreground flex-shrink-0">
      <span className="text-xs">{start}–{end} sur {totalCount}</span>
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} className="w-7 h-7 flex items-center justify-center rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} className="w-7 h-7 flex items-center justify-center rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export const EmailList = ({
  emails, loading, selectedEmail, onEmailSelect, onStarEmail,
  onDeleteEmail, onMarkAsRead, onBulkDelete, onBulkMarkRead, onBulkMarkUnread,
  onBulkStar, onBulkImportant, onBulkArchive, onBulkSpam, onRefresh,
  currentView, folderName,
  currentPage, totalPages, totalCount, pageSize, onPageChange,
  listRef
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const selectionMode = selectedIds.size > 0;

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }, []);

  const selectAll = () => {
    if (selectedIds.size === emails.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(emails.map(e => e.id)));
    }
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkMarkRead = async () => {
    setBulkLoading(true);
    await (onBulkMarkRead ? onBulkMarkRead(selectedIds) : Promise.resolve());
    setBulkLoading(false);
    clearSelection();
  };

  const runBulkAction = async (action) => {
    setBulkLoading(true);
    await (action ? action(selectedIds) : Promise.resolve());
    setBulkLoading(false);
    clearSelection();
  };

  const handleBulkDelete = async () => {
    setBulkLoading(true);
    await (onBulkDelete ? onBulkDelete(selectedIds) : Promise.resolve());
    setBulkLoading(false);
    clearSelection();
  };

  const getViewTitle = () => {
    if (currentView === 'starred') return 'Favoris';
    if (currentView === 'important') return 'Important';
    return folderName;
  };

  const allSelected = emails.length > 0 && selectedIds.size === emails.length;

  if (loading && emails.length === 0) {
    return (
      <div className="flex flex-col h-full bg-background items-center justify-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mb-3 text-primary" />
        <p className="text-sm font-medium">Chargement des messages...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background" data-testid="email-list-panel">
      {/* Header normal ou barre de sélection bulk */}
      {selectionMode ? (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border flex-shrink-0 bg-primary/10">
          <button onClick={clearSelection} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-accent flex-shrink-0">
            <X className="w-4 h-4 text-foreground" />
          </button>
          <span className="text-[14px] font-semibold text-foreground flex-1">{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
          <button
            onClick={handleBulkMarkRead}
            disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border text-[12px] font-medium hover:bg-accent disabled:opacity-50 transition-colors"
          >
            <MailOpen className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Marquer lu</span>
          </button>
          <button
            onClick={() => runBulkAction(onBulkMarkUnread)}
            disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border text-[12px] font-medium hover:bg-accent disabled:opacity-50 transition-colors"
          >
            <MailCheck className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Non lu</span>
          </button>
          <button
            onClick={() => runBulkAction(onBulkStar)}
            disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border text-[12px] font-medium hover:bg-accent disabled:opacity-50 transition-colors"
          >
            <Star className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Favori</span>
          </button>
          <button
            onClick={() => runBulkAction(onBulkImportant)}
            disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border text-[12px] font-medium hover:bg-accent disabled:opacity-50 transition-colors"
          >
            <AlertTriangle className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Important</span>
          </button>
          <button
            onClick={() => runBulkAction(onBulkArchive)}
            disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border text-[12px] font-medium hover:bg-accent disabled:opacity-50 transition-colors"
          >
            <Archive className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Archiver</span>
          </button>
          <button
            onClick={() => runBulkAction(onBulkSpam)}
            disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background border border-border text-[12px] font-medium hover:bg-accent disabled:opacity-50 transition-colors"
          >
            <AlertOctagon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Spam</span>
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={bulkLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 text-white text-[12px] font-medium hover:bg-red-600 disabled:opacity-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Supprimer</span>
          </button>
          <button
            onClick={selectAll}
            className="px-3 py-1.5 rounded-lg bg-background border border-border text-[12px] font-medium hover:bg-accent transition-colors hidden sm:flex"
          >
            {allSelected ? 'Désélect. tout' : 'Tout sélect.'}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between px-4 py-2 border-b border-border flex-shrink-0 bg-card">
          <h2 className="text-[15px] font-medium text-foreground">{getViewTitle()}</h2>
          <div className="flex items-center gap-2">
            {/* Badge non lus */}
            {emails.filter(e => !e.is_read).length > 0 && (
              <span className="text-[11px] bg-primary text-primary-foreground font-bold px-2 py-0.5 rounded-full">
                {emails.filter(e => !e.is_read).length} non lu{emails.filter(e => !e.is_read).length > 1 ? 's' : ''}
              </span>
            )}
            <span className="text-xs text-muted-foreground">{totalCount} message{totalCount !== 1 ? 's' : ''}</span>
          </div>
        </div>
      )}

      {/* Pagination */}
      <Pagination currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} pageSize={pageSize} onPageChange={onPageChange} />

      {/* List */}
      <div ref={listRef} className="flex-1 overflow-y-auto min-h-0">
        {emails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Mail className="w-12 h-12 mb-4 text-muted-foreground/30" />
            <p className="text-sm font-medium text-muted-foreground">Aucun message</p>
            <p className="text-xs text-muted-foreground/60 mt-1">Ce dossier est vide</p>
          </div>
        ) : emails.map((email) => (
          <EmailRow
            key={email.id}
            email={email}
            isSelected={selectedEmail?.id === email.id}
            isBulkSelected={selectedIds.has(email.id)}
            selectionMode={selectionMode}
            onSelect={() => {
              if (selectionMode) { toggleSelect(email.id); } else { onEmailSelect(email); }
            }}
            onBulkSelect={() => toggleSelect(email.id)}
            onLongPress={() => toggleSelect(email.id)}
            onStar={(e) => { e.stopPropagation(); onStarEmail(email.id, !email.is_starred); }}
            onDelete={(e) => { e.stopPropagation(); onDeleteEmail(email.id); }}
            onMarkRead={(e) => { e.stopPropagation(); onMarkAsRead(email.id); }}
          />
        ))}
      </div>
    </div>
  );
};

const EmailRow = ({
  email, isSelected, isBulkSelected, selectionMode,
  onSelect, onBulkSelect, onLongPress, onStar, onDelete, onMarkRead
}) => {
  const isUnread = !email.is_read;
  const longPressTimer = useRef(null);
  const didLongPress = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });

  const handleTouchStart = (e) => {
    didLongPress.current = false;
    startPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      onLongPress();
    }, 500);
  };

  const handleTouchMove = (e) => {
    const dx = Math.abs(e.touches[0].clientX - startPos.current.x);
    const dy = Math.abs(e.touches[0].clientY - startPos.current.y);
    if (dx > 8 || dy > 8) clearTimeout(longPressTimer.current);
  };

  const handleTouchEnd = () => {
    clearTimeout(longPressTimer.current);
  };

  const handleClick = (e) => {
    if (didLongPress.current) { didLongPress.current = false; return; }
    onSelect(e);
  };

  const rowBg = isBulkSelected
    ? 'rgba(255,189,23,0.45)'
    : isSelected
      ? 'rgba(255,189,23,0.35)'
      : isUnread
        ? 'rgba(255,189,23,0.10)'
        : 'transparent';
  const rowShadow = isUnread && !isSelected && !isBulkSelected ? 'inset 3px 0 0 #ffbd17' : 'none';

  return (
    <div
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onSelect(); }}
      data-testid={`email-item-${email.id}`}
      className="relative cursor-pointer border-b border-border select-none"
      style={{ background: rowBg, boxShadow: rowShadow }}
    >
      {/* ──── DESKTOP layout (md+) ──── */}
      <div className="hidden md:flex items-center">
        <div style={{ width: 8 }} />
        {/* Checkbox + Star fixes: aucun élément ne change au survol */}
        <div className="w-12 mr-1 flex-shrink-0 flex items-center justify-center gap-1">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onBulkSelect(); }}
            className={cn(
              "w-5 h-5 rounded flex items-center justify-center border-2 cursor-pointer",
              isBulkSelected ? "bg-primary border-primary" : "border-border bg-background"
            )}
            title="Sélectionner"
          >
            {isBulkSelected && <Check className="w-3 h-3 text-primary-foreground" />}
          </button>
          {!selectionMode && (
            <button
              onClick={onStar}
              className="w-5 h-5 flex items-center justify-center"
              title={email.is_starred ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            >
              <Star className="w-4 h-4" style={{ fill: email.is_starred ? '#f6ad55' : 'none', color: email.is_starred ? '#f6ad55' : 'hsl(var(--muted-foreground))' }} />
            </button>
          )}
        </div>
        {/* Avatar */}
        <div className="px-2 flex-shrink-0"><SenderAvatar email={email} selected={isBulkSelected} selectionMode={false} /></div>
        {/* Content */}
        <div className="flex-1 min-w-0 flex items-center gap-4 py-3 pr-3">
          <span className="w-[150px] flex-shrink-0 truncate text-[13px]" style={{ fontWeight: isUnread ? 800 : 400, color: isUnread ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))' }}>
            {email.from_name || email.from_email}
          </span>
          <span className="flex-1 min-w-0 truncate text-[13px]">
            <span style={{ fontWeight: isUnread ? 700 : 400, color: isUnread ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))' }}>
              {email.subject || '(Sans objet)'}
            </span>
            <span className={isUnread ? 'text-muted-foreground font-normal' : 'text-muted-foreground/60 font-normal'}>
              {' '}— {truncate(email.body_preview, isUnread ? 60 : 80)}
            </span>
          </span>
          {email.is_important && <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" title="Important" />}
          {email.attachments?.length > 0 && <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
          <div className="w-16 flex items-center justify-end flex-shrink-0">
            <span className="text-[12px] tabular-nums text-right" style={{ fontWeight: isUnread ? 700 : 400, color: isUnread ? '#ffbd17' : 'hsl(var(--muted-foreground))' }}>
              {formatDate(email.created_at)}
            </span>
          </div>
        </div>
      </div>

      {/* ──── MOBILE layout (< md) ──── */}
      <div className="flex md:hidden items-center gap-2 px-3 py-3 min-h-[72px]">
        {/* Avatar / checkbox (long press active la sélection) */}
        <div className="relative flex-shrink-0">
          <SenderAvatar
            email={email}
            selected={isBulkSelected}
            selectionMode={selectionMode}
            onSelect={onBulkSelect}
          />
          {/* Point non lu */}
          {isUnread && !selectionMode && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary border-2 border-background" />
          )}
        </div>
        {/* Text block */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-[14px]" style={{ fontWeight: isUnread ? 700 : 500, color: isUnread ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))' }}>
              {email.from_name || email.from_email}
            </span>
            <span className="text-[12px] flex-shrink-0 tabular-nums" style={{ fontWeight: isUnread ? 700 : 400, color: isUnread ? '#ffbd17' : 'hsl(var(--muted-foreground))' }}>
              {formatDate(email.created_at)}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="truncate text-[13px]" style={{ fontWeight: isUnread ? 600 : 400, color: isUnread ? 'hsl(var(--foreground))' : 'hsl(var(--muted-foreground))' }}>
              {email.subject || '(Sans objet)'}
            </span>
            {email.attachments?.length > 0 && <Paperclip className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
            {email.is_important && <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />}
          </div>
          <p className="truncate text-[12px] text-muted-foreground/70 mt-0.5">
            {truncate(email.body_preview, 60)}
          </p>
        </div>
        {/* Star (si pas en mode sélection) */}
        {!selectionMode && (
          <button
            onClick={e => { e.stopPropagation(); onStar(e); }}
            className="flex-shrink-0 p-1"
            style={{ opacity: email.is_starred ? 1 : 0.25 }}
          >
            <Star className="w-4 h-4" style={{ fill: email.is_starred ? '#f6ad55' : 'none', color: email.is_starred ? '#f6ad55' : 'hsl(var(--muted-foreground))' }} />
          </button>
        )}
      </div>
    </div>
  );
};
